import React from 'react';
import { Link } from '@tanstack/react-router';
import Header from './Home/components/Header';
import { 
  DocumentTextIcon,
  CodeBracketIcon,
  BookOpenIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline';
import { useSEO } from '../hooks/useSEO';

const DocCard = ({ icon: Icon, title, description, linkTo, linkText, isComingSoon = false }) => {
  const baseClasses = "bg-background-elevated border border-border/60 rounded-xl p-6 shadow-sm hover:shadow-md transition-all duration-200";
  const interactiveClasses = "hover:border-border group";
  const disabledClasses = "opacity-60";
  
  const cardClasses = `${baseClasses} ${isComingSoon ? disabledClasses : interactiveClasses}`;
  
  const content = (
    <>
      <div className="flex items-center mb-4">
        <Icon className={`h-8 w-8 mr-3 ${isComingSoon ? 'text-muted-foreground/50' : 'text-accent'}`} />
        <h3 className={`text-xl font-semibold ${isComingSoon ? 'text-muted-foreground/50' : 'text-foreground group-hover:text-accent transition-colors duration-200'}`}>
          {title}
        </h3>
      </div>
      <p className={`mb-4 ${isComingSoon ? 'text-muted-foreground/50' : 'text-muted-foreground/90'}`}>
        {description}
      </p>
      <div className={`text-sm font-medium ${isComingSoon ? 'text-muted-foreground/50' : 'text-accent'}`}>
        {linkText || (isComingSoon ? 'Coming soon' : 'View →')}
      </div>
    </>
  );

  if (isComingSoon || !linkTo) {
    return <div className={cardClasses}>{content}</div>;
  }

  return (
    <Link to={linkTo} className={cardClasses}>
      {content}
    </Link>
  );
};


export default function DocsIndex() {
  useSEO({
    title: 'Documentation - Compress GLB | API Docs, Support & Guides',
    description: 'Complete documentation for Compress GLB including API reference, support guides, troubleshooting, and integration tutorials for 3D model compression.',
    keywords: 'Compress GLB documentation, API docs, 3D compression guide, GLB API, GLTF compression tutorial',
    canonical: 'https://compress-glb.com/docs',
    ogImage: '/images/og/docs.png',
    ogTitle: 'Compress GLB Documentation',
    ogDescription: 'Complete documentation and guides for integrating 3D model compression into your workflow.',
    structuredData: {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Compress GLB Documentation",
      "description": "Complete documentation for 3D model compression API and tools",
      "url": "https://compress-glb.com/docs"
    }
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-foreground mb-4 tracking-tight">Documentation</h1>
          <p className="text-xl text-muted-foreground/90">
            Everything you need to integrate GLB compression into your workflow
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <DocCard
            icon={DocumentTextIcon}
            title="Support docs"
            description="Troubleshooting guides, billing information, and support resources to help you get the most out of this service."
            linkTo="/docs/support"
            linkText="View support docs →"
          />

          <DocCard
            icon={BookOpenIcon}
            title="Guides"
            description="Step-by-step tutorials and best practices for optimizing your 3D models and workflows."
            isComingSoon={true}
          />

          <DocCard
            icon={CodeBracketIcon}
            title="API docs"
            description="Complete API reference with endpoints, authentication, and code examples for integrating this compression service."
            linkTo="/docs/api"
            linkText="View API documentation →"
          />

          <DocCard
            icon={QuestionMarkCircleIcon}
            title="FAQ"
            description="Frequently asked questions about compression settings, file formats, and common issues."
            linkTo="/#faq"
            linkText="View FAQ →"
          />
        </div>


      </div>
    </div>
  );
} 